// "There is a newer version of this app" — said where a device can see it.
//
// Ported from the Andon app. The worker is registered with `prompt` rather
// than `autoUpdate`, because swapping the bundle out from under somebody
// mid-pod is the one moment this app must not blink — and a prompt is only
// honest if the choice reaches the person. A phone is the device MOST likely
// to be running an old bundle, because it is launched once and then lives in
// the app switcher for a week, so this is mounted once, above every surface,
// and interrupts briefly and politely: in the accent rather than the danger
// colour, because an out-of-date app is not an emergency.
//
// WHY IT CAN BE DISMISSED
// -----------------------
// Because the alternative is worse. A banner that cannot be got rid of, on top
// of a screen somebody is using to sign a pod off, teaches people to install
// updates at the worst possible moment — or to work around the app. Reloading
// mid-pod is exactly what the prompt policy exists to prevent, and that policy
// is only honest if "not now" is a real answer.
//
// Dismissal is per page load and is not remembered. It comes back on the next
// launch, and a newer build than the one waved away asks again on its own
// account (`setUpdateReady` clears the flag).

import { Icon } from '@/components/ui/Icon';
import { useT } from '@/i18n/useT';
import { applyPendingUpdate } from '@/pwa';
import { useStore } from '@/store';

export function UpdateBanner() {
  const t = useT();
  const updateReady = useStore((s) => s.updateReady);
  const updateDismissed = useStore((s) => s.updateDismissed);
  const dismissUpdate = useStore((s) => s.dismissUpdate);

  if (!updateReady || updateDismissed) return null;

  return (
    <div
      role="status"
      style={{
        position: 'fixed',
        // Top, not bottom. Every phone surface here ends in the tab bar, and
        // covering the control somebody reached for is worse than covering a
        // heading. The inset keeps it clear of the notch on an iPhone and of
        // the status bar on an installed Android PWA.
        // Clear of the staging strip too, which is 0px everywhere but staging.
        top: 'calc(env(safe-area-inset-top) + 8px + var(--sf-banner, 0px))',
        left: 10,
        right: 10,
        // Under the sheets on purpose, and above the push banner: one of the
        // two at a time, and an update is the rarer of them.
        zIndex: 25,
        padding: '11px 12px 11px 14px',
        borderRadius: 15,
        background: 'var(--surface)',
        border: '1px solid var(--accent)',
        boxShadow: '0 8px 24px rgba(22,24,28,.18)',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        animation: 'ipRise .3s ease',
      }}
    >
      <Icon name="undo" size={17} strokeWidth={2.2} style={{ color: 'var(--accent)', flex: 'none' }} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', font: "700 13px 'Space Grotesk',sans-serif", color: 'var(--ink)' }}>{t.updateReadyTitleT}</span>
        <span style={{ display: 'block', font: '500 11px Manrope,sans-serif', color: 'var(--sub)', marginTop: 1 }}>{t.updateReadyBodyT}</span>
      </span>
      <button
        type="button"
        className="press"
        onClick={applyPendingUpdate}
        style={{
          height: 36,
          padding: '0 14px',
          borderRadius: 11,
          background: 'var(--accent)',
          color: 'var(--accentInk)',
          border: 0,
          font: "600 12px 'Space Grotesk',sans-serif",
          flex: 'none',
        }}
      >
        {t.installUpdateT}
      </button>
      {/* A 36px target, not a 16px glyph. This is tapped with a glove on. */}
      <button
        type="button"
        className="press"
        aria-label={t.notNowT}
        onClick={dismissUpdate}
        style={{
          width: 36,
          height: 36,
          borderRadius: 11,
          background: 'var(--surface2)',
          border: '1px solid var(--line)',
          color: 'var(--sub)',
          display: 'grid',
          placeItems: 'center',
          flex: 'none',
        }}
      >
        <Icon name="cross" size={13} strokeWidth={2.6} />
      </button>
    </div>
  );
}
